// shared-lib/millwork/openingSides.js
/**
 * Which room each side of an opening faces — the pure twin of
 * `web-app/src/utils/openingSideRooms.js`, which now delegates here.
 * Side A is the opening's local +Z, Side B −Z. Doors and windows are
 * normalised so +Z faces the priority room: a door or window whose
 * `wallFacesPriorityRoom === false` is turned by π (Door.jsx / Window.jsx).
 * A cased opening is not.
 *
 * Pure.
 */
const { isPointInsidePolygon } = require('../geometry/pointInPolygon');

// Past the wall body and any casing proud of it, still inside a 3 ft closet.
const PROBE_DISTANCE_M = 0.45;

/**
 * The plan direction of local +Z under three.js's XYZ Euler order:
 * (0,0,1) ↦ (sin y, −sin x·cos y, cos x·cos y); z turns leave +Z alone.
 */
const sideADirection = (element) => {
  const rotation = element?.rotation;
  if (!rotation || typeof rotation.y !== 'number') return null;
  const flips = !!(element.category?.door || element.category?.window);
  const yaw =
    rotation.y +
    (flips && element.wallFacesPriorityRoom === false ? Math.PI : 0);
  const x = Math.sin(yaw);
  const z = Math.cos(rotation.x || 0) * Math.cos(yaw);
  const len = Math.hypot(x, z);
  if (len < 1e-9) return null;
  return { x: x / len, z: z / len };
};

const floorPolygons = (room) =>
  (room?.floors || [])
    .map((floor) => floor?.polygonCorners)
    .filter((corners) => Array.isArray(corners) && corners.length >= 3);

const roomAtPoint = (point, rooms) =>
  rooms.find((room) =>
    floorPolygons(room).some((poly) => isPointInsidePolygon(point, poly))
  ) || null;

/**
 * The probe point a short way off each face of an opening, or null when the
 * element carries no centre or rotation.
 */
const sideProbePoints = (element, { wallThicknessM = 0 } = {}) => {
  const center = element?.center;
  if (typeof center?.x !== 'number' || typeof center?.z !== 'number')
    return null;
  const dir = sideADirection(element);
  if (!dir) return null;
  const reach = Math.max(wallThicknessM, 0) / 2 + PROBE_DISTANCE_M;
  const at = (sign) => ({
    x: center.x + dir.x * reach * sign,
    z: center.z + dir.z * reach * sign,
  });
  return { A: at(1), B: at(-1) };
};

/** Whether `room`'s own floor holds the point (no other room is consulted). */
const roomHoldsPoint = (room, point) =>
  floorPolygons(room).some((poly) => isPointInsidePolygon(point, poly));

const holdsOpening = (room, element) =>
  ['doors', 'windows', 'openings'].some((key) =>
    (room?.[key] || []).some(
      (el) => el?.id === element.id || el?.structureOpeningId === element.id
    )
  );

/**
 * @returns {{A: Object|null, B: Object|null}} per side `{ kind: 'room', room }`,
 *   `{ kind: 'exterior' }`, or null when the geometry to answer is missing —
 *   null means UNKNOWN, never exterior.
 */
const openingSideRooms = (element, rooms, { wallThicknessM = 0 } = {}) => {
  const unknown = { A: null, B: null };
  const center = element?.center;
  if (typeof center?.x !== 'number' || typeof center?.z !== 'number')
    return unknown;
  const dir = sideADirection(element);
  if (!dir) return unknown;
  const reach = Math.max(wallThicknessM, 0) / 2 + PROBE_DISTANCE_M;
  const allRooms = rooms || [];
  // Only rooms on the opening's own storey: floor polygons carry no height.
  const ownRoom = allRooms.find((room) => holdsOpening(room, element));
  const candidates =
    ownRoom && typeof ownRoom.story === 'number'
      ? allRooms.filter((r) => r.story === ownRoom.story)
      : allRooms;
  const probe = (sign) => {
    const room = roomAtPoint(
      {
        x: center.x + dir.x * reach * sign,
        z: center.z + dir.z * reach * sign,
      },
      candidates
    );
    return room ? { kind: 'room', room } : { kind: 'exterior' };
  };
  return { A: probe(1), B: probe(-1) };
};

module.exports = {
  sideADirection,
  sideProbePoints,
  roomHoldsPoint,
  openingSideRooms,
  PROBE_DISTANCE_M,
};
